<template>
  <section class="workspace">
    <div class="workspace-head">
      <div>
        <span class="eyebrow">Layer Library</span>
        <h3>{{ tabTitle }}</h3>
      </div>
      <div class="tabs">
        <button :class="{ active: tab === 'archive' }" @click="$emit('update:tab', 'archive')">层级档案</button>
        <button :class="{ active: tab === 'mine' }" @click="$emit('openMine')">我的层级</button>
        <button :class="{ active: tab === 'favorite' }" @click="$emit('openFavorites')">我的收藏</button>
      </div>
    </div>

    <div v-if="tab === 'archive'" class="filters">
      <el-select class="search-field-select" :model-value="searchField" @update:model-value="updateSearchField">
        <el-option label="按名称" value="name" />
        <el-option label="按层级" value="levelNum" />
      </el-select>
      <el-input :model-value="keyword" :placeholder="searchPlaceholder" clearable @update:model-value="updateKeyword" />
      <el-select class="status-filter-select" :model-value="statusFilter" placeholder="全部" @update:model-value="updateStatusFilter">
        <el-option label="全部" :value="''" />
        <el-option label="未占领" :value="0" />
        <el-option label="已占领" :value="1" />
        <el-option label="待上线" :value="2" />
      </el-select>
    </div>

    <div class="level-grid">
      <article v-for="level in levels" :key="level.id" class="level-card" @click="$emit('openLevel', level)">
        <div class="cover">
          <img v-if="level.img" :src="level.img" :alt="level.name" />
          <div v-else class="empty-cover">Level {{ level.levelNum || level.id }}</div>
          <span class="status-badge">{{ statusText(level.status) }}</span>
          <span v-if="Number(level.auditStatus) === 1" class="audit-badge">待审核</span>
        </div>
        <div class="card-body">
          <small>Level {{ level.levelNum || level.id }}</small>
          <h4>{{ level.title || level.name || '未命名层级' }}</h4>
          <p>{{ level.description || '暂无简介' }}</p>
          <div class="card-stats">
            <span>{{ level.enterNum || 0 }} 访问</span>
            <span>{{ level.messageNum || 0 }} 留言</span>
            <span>{{ level.favoriteNum || 0 }} 收藏</span>
          </div>
          <div v-if="tab === 'mine'" class="patrol-note">
            <span>上次巡逻：{{ formatDate(level.guardedAt) }}</span>
            <span class="return-note" :class="returnNoteClass(level)">
              {{ returnToOpenText(level) }}
            </span>
          </div>
          <button v-if="tab === 'mine'" class="edit-level-btn patrol-level-btn" @click.stop="$emit('patrolLevel', level)">巡逻层级</button>
          <button v-if="tab === 'mine'" class="edit-level-btn" @click.stop="$emit('openEdit', level)">编辑层级</button>
        </div>
      </article>
      <el-empty v-if="levels.length === 0" description="暂无层级数据" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { LevelItem, LevelTab, SearchField } from './types';

const props = defineProps<{
  tab: LevelTab;
  tabTitle: string;
  searchField: SearchField;
  keyword: string;
  statusFilter: number | '';
  levels: LevelItem[];
  patrolExpireDays: number;
}>();

const emit = defineEmits<{
  (e: 'update:tab', value: LevelTab): void;
  (e: 'update:searchField', value: SearchField): void;
  (e: 'update:keyword', value: string): void;
  (e: 'update:statusFilter', value: number | ''): void;
  (e: 'loadArchive'): void;
  (e: 'openMine'): void;
  (e: 'openFavorites'): void;
  (e: 'openLevel', value: LevelItem): void;
  (e: 'openEdit', value: LevelItem): void;
  (e: 'patrolLevel', value: LevelItem): void;
}>();

const searchPlaceholder = computed(() => {
  return props.searchField === 'levelNum' ? '输入层级编号' : '输入层级名称';
});

const updateSearchField = (value: string) => {
  emit('update:searchField', value === 'levelNum' ? 'levelNum' : 'name');
  emit('loadArchive');
};

const updateKeyword = (value: string | number) => {
  emit('update:keyword', String(value));
  emit('loadArchive');
};

const updateStatusFilter = (value: number | string) => {
  emit('update:statusFilter', value === '' ? '' : Number(value));
  emit('loadArchive');
};

const statusText = (status: number) => {
  if (status === 1) return '已占领';
  if (status === 2) return '待上线';
  if (status === -1) return '已删除';
  return '未占领';
};

const parseDate = (value?: string) => {
  if (!value) return null;
  const normalized = String(value).replace('T', ' ').slice(0, 19).replace(/-/g, '/');
  const time = new Date(normalized).getTime();
  return Number.isNaN(time) ? null : new Date(time);
};

const pad = (value: number) => String(value).padStart(2, '0');

const formatDate = (value?: string | Date | null) => {
  if (!value) return '暂无';
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) return '暂无';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const returnBaseDate = (level: LevelItem) => {
  return parseDate(level.guardedAt || level.occupiedAt || level.updateAt || level.createAt);
};

const returnToOpenDate = (level: LevelItem) => {
  const base = returnBaseDate(level);
  if (!base || props.patrolExpireDays <= 0) return null;
  return new Date(base.getTime() + props.patrolExpireDays * 24 * 60 * 60 * 1000);
};

const returnNoteClass = (level: LevelItem) => {
  const date = returnToOpenDate(level);
  if (!date) return '';
  const diff = date.getTime() - Date.now();
  if (diff <= 0) return 'is-expired';
  if (diff <= 24 * 60 * 60 * 1000) return 'is-soon';
  return '';
};

const returnToOpenText = (level: LevelItem) => {
  const date = returnToOpenDate(level);
  if (!date) return '预计回归未占领：暂无';
  if (date.getTime() <= Date.now()) return '已到回归未占领时间，刷新后将自动释放';
  return `预计回归未占领：${formatDate(date)}`;
};
</script>
